import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser, useAuth } from "@clerk/clerk-react";
import { useRef } from "react";
import { toast } from "sonner";
import { freelancerService } from "@/lib/api/services/freelancer.service";
import { embeddingService } from "@/lib/api/services/embedding.service";
import { emailService } from "@/lib/api/services/email.service";
import type { Database } from "@/lib/supabase/supabase-types";

// Helper function to generate embeddings for a profile
async function generateEmbeddings(profileId: string): Promise<void> {
  try {
    // Use the new API service
    await embeddingService.generateProfileEmbedding(profileId);
    console.log(`Successfully generated embeddings for profile ${profileId}`);
  } catch (error) {
    // Log error but don't throw - embedding generation is non-critical
    console.error('Failed to generate embeddings:', error);
  }
}

type FreelancerProfile = Database["public"]["Tables"]["freelancer_profiles"]["Row"];
type FreelancerProfileInsert = Database["public"]["Tables"]["freelancer_profiles"]["Insert"];
type FreelancerProfileUpdate = Database["public"]["Tables"]["freelancer_profiles"]["Update"];

// Extended type that includes enabled status, featured status, review fields, and view count
// Note: review fields may not exist in database yet - handled as optional
type FreelancerProfileWithEnabled = FreelancerProfile & {
  is_enabled: boolean;
  enabled_at: string | null;
  is_featured: boolean;
  featured_at: string | null;
  // Review fields (optional - may not be in database yet)
  review_status?: string | null;
  review_comments?: string | null;
  submitted_for_review_at?: string | null;
  reviewed_at?: string | null;
  // View count (optional - may not be in database yet)
  view_count?: number;
};

/**
 * Unified freelancer profile hook that works with profile IDs
 * This replaces both useFreelancerProfile and useFreelancerProfileById
 *
 * @param profileId - The freelancer profile ID (UUID from postgres)
 * @param options - Optional configuration
 * @param options.enableMutations - Whether to enable create/update mutations (default: false for public profiles)
 */
export function useFreelancerProfile(
  profileId: string | null | undefined,
  options: { enableMutations?: boolean } = {}
) {
  const { user } = useUser();
  const { sessionClaims } = useAuth();
  const queryClient = useQueryClient();
  // Ref to track if we should suppress the creation toast (e.g., during onboarding)
  const suppressCreateToastRef = useRef(false);

  // Check if user is admin - check both JWT and fresh publicMetadata to handle stale JWT on first login
  const isAdmin = sessionClaims?.app_role === 'admin' || user?.publicMetadata?.app_role === 'admin';

  // Get the profile data
  const profileQuery = useQuery({
    queryKey: ["freelancer-profile", profileId],
    queryFn: async () => {
      if (!profileId) throw new Error("No profile ID");

      // Use backend API to fetch profile
      const data = await freelancerService.getProfile(profileId);

      // Backend API should return profile with enabled/featured status
      // Transform to match expected type
      const profileWithEnabled: FreelancerProfileWithEnabled = {
        ...data,
        is_enabled: (data as { is_enabled?: boolean }).is_enabled ?? false,
        enabled_at: (data as { enabled_at?: string | null }).enabled_at ?? null,
        is_featured: (data as { is_featured?: boolean }).is_featured ?? false,
        featured_at: (data as { featured_at?: string | null }).featured_at ?? null,
        view_count: (data as { view_count?: number }).view_count ?? 0,
      };

      return profileWithEnabled;
    },
    enabled: !!profileId,
    staleTime: 60 * 1000, // 1 minute
  });

  // Determine ownership and viewing permissions
  const profile = profileQuery.data;
  // User is owner if profile belongs to them OR if no profile exists yet (so they can create one)
  const isOwner = profile ? profile.user_id === user?.id : !!user?.id;
  const canView = profile && (profile.is_searchable || isOwner || isAdmin);

  // Create profile mutation (only for owners)
  const createProfileMutation = useMutation({
    mutationFn: async (profileData: Omit<FreelancerProfileInsert, 'user_id'>) => {
      if (!user?.id) throw new Error("No user ID");

      const data = await freelancerService.createProfile(profileData);

      // Backend API should return profile with enabled/featured status
      // Transform to match expected type
      const profileWithEnabled: FreelancerProfileWithEnabled = {
        ...data,
        is_enabled: (data as any).is_enabled ?? false,
        enabled_at: (data as any).enabled_at ?? null,
        is_featured: (data as any).is_featured ?? false,
        featured_at: (data as any).featured_at ?? null,
      };

      return profileWithEnabled;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["freelancer-profile", data.id], data);
      // Invalidate admin list to keep it in sync
      queryClient.invalidateQueries({ queryKey: ["freelancer-profiles"] });
      // Invalidate freelancer profile ID query so useRole gets the new profile ID
      queryClient.invalidateQueries({ queryKey: ["freelancer-profile-id", user?.id] });
      // Also set the profile ID directly in cache for immediate availability
      queryClient.setQueryData(["freelancer-profile-id", user?.id], data.id);
      
      // Only show toast if not suppressed (e.g., during onboarding)
      if (!suppressCreateToastRef.current) {
        toast.success('Freelancer profile created');
      }
      suppressCreateToastRef.current = false;

      // Generate embeddings in the background (non-blocking)
      generateEmbeddings(data.id);
    },
    onError: (error) => {
      console.error('Failed to create freelancer profile:', error);
      toast.error('Failed to create freelancer profile');
    },
  });

  // Update profile mutation (only for owners)
  const updateProfileMutation = useMutation({
    mutationFn: async (updates: FreelancerProfileUpdate) => {
      if (!user?.id) throw new Error("No user ID");

      const data = await freelancerService.updateProfile(updates);

      // Backend API should return profile with enabled/featured status
      // Transform to match expected type
      const profileWithEnabled: FreelancerProfileWithEnabled = {
        ...data,
        is_enabled: (data as any).is_enabled ?? false,
        enabled_at: (data as any).enabled_at ?? null,
        is_featured: (data as any).is_featured ?? false,
        featured_at: (data as any).featured_at ?? null,
      };

      return profileWithEnabled;
    },
    onMutate: async (updates) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["freelancer-profile", profileId] });

      // Snapshot the previous value
      const previousProfile = queryClient.getQueryData<FreelancerProfileWithEnabled>(["freelancer-profile", profileId]);

      // Optimistically update to the new value
      if (previousProfile) {
        const optimisticData = {
          ...previousProfile,
          ...updates,
        };
        queryClient.setQueryData<FreelancerProfileWithEnabled>(["freelancer-profile", profileId], optimisticData);
      }

      // Return a context object with the snapshotted value
      return { previousProfile };
    },
    onError: (err, _updates, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousProfile) {
        queryClient.setQueryData(["freelancer-profile", profileId], context.previousProfile);
      }
      console.error('Freelancer profile update failed:', err);
      toast.error('Failed to update freelancer profile. Please try again.');
    },
    onSuccess: (data) => {
      // Update with the actual server data
      queryClient.setQueryData(["freelancer-profile", profileId], data);
      // Also invalidate the query to ensure all subscribers get fresh data
      // This helps with profile completion updates across different pages
      queryClient.invalidateQueries({ queryKey: ["freelancer-profile", profileId] });
      // Invalidate admin list to keep it in sync
      queryClient.invalidateQueries({ queryKey: ["freelancer-profiles"] });
      toast.success('Freelancer profile updated');

      // Generate embeddings in the background (non-blocking)
      generateEmbeddings(data.id);
    },
  });

  // Submit profile for review mutation
  const submitForReviewMutation = useMutation({
    mutationFn: async () => {
      if (!profileId) throw new Error("No profile ID");
      if (!isOwner) throw new Error("Not authorized");

      // Use backend API to update review status
      const data = await freelancerService.updateProfile({
        review_status: 'submitted',
        submitted_for_review_at: new Date().toISOString(),
      } as FreelancerProfileUpdate);

      return data;
    },
    onSuccess: (data) => {
      // Transform to include enabled/featured status
      const profileWithEnabled: FreelancerProfileWithEnabled = {
        ...data,
        is_enabled: (data as any).is_enabled ?? false,
        enabled_at: (data as any).enabled_at ?? null,
        is_featured: (data as any).is_featured ?? false,
        featured_at: (data as any).featured_at ?? null,
      };
      queryClient.setQueryData(["freelancer-profile", profileId], profileWithEnabled);
      queryClient.invalidateQueries({ queryKey: ["admin-freelancer-profiles"] });
      toast.success('Profile submitted for review');
      
      // Send email notification (non-blocking)
      if (data.email) {
        emailService.sendProfileSubmittedEmail(data.email).catch((error) => {
          console.error('Failed to send profile submitted email:', error);
          // Don't show error to user, email sending is non-critical
        });
      }
    },
    onError: (err) => {
      console.error('Failed to submit profile for review:', err);
      toast.error('Failed to submit profile for review. Please try again.');
    },
  });

  // Helper to create or update profile (only available if mutations enabled and is owner)
  const saveProfile = async (
    data: Omit<FreelancerProfileInsert, 'user_id'> | FreelancerProfileUpdate,
    saveOptions?: { suppressToast?: boolean }
  ): Promise<FreelancerProfileWithEnabled> => {
    if (!options.enableMutations || !isOwner) {
      throw new Error('Profile mutations not enabled or not authorized');
    }

    if (profile) {
      // Profile exists, update it
      return await updateProfileMutation.mutateAsync(data as FreelancerProfileUpdate);
    } else {
      // Profile doesn't exist, create it
      // Set suppress toast flag if requested
      suppressCreateToastRef.current = saveOptions?.suppressToast ?? false;
      return await createProfileMutation.mutateAsync(data as Omit<FreelancerProfileInsert, 'user_id'>);
    }
  };

  return {
    // Data
    freelancerProfile: canView ? profile : null,
    isLoading: profileQuery.isLoading,
    error: canView === false ? new Error('Profile not accessible') : profileQuery.error,

    // Approval status
    isEnabled: profile?.is_enabled || false,
    enabledAt: profile?.enabled_at || null,

    // Featured status
    isFeatured: profile?.is_featured || false,
    featuredAt: profile?.featured_at || null,

    // Review status
    reviewStatus: profile?.review_status || 'draft',
    reviewComments: profile?.review_comments || null,
    submittedForReviewAt: profile?.submitted_for_review_at || null,
    reviewedAt: profile?.reviewed_at || null,

    // Permissions
    isOwner,
    isAdmin,
    canView,

    // Mutations (only available if enabled and authorized)
    createFreelancerProfile: (options.enableMutations && isOwner) ? createProfileMutation.mutate : undefined,
    updateFreelancerProfile: (options.enableMutations && isOwner) ? updateProfileMutation.mutate : undefined,
    saveFreelancerProfile: (options.enableMutations && isOwner) ? saveProfile : undefined,
    submitForReview: (options.enableMutations && isOwner) ? submitForReviewMutation.mutate : undefined,
    isCreating: createProfileMutation.isPending,
    isUpdating: updateProfileMutation.isPending,
    isSaving: createProfileMutation.isPending || updateProfileMutation.isPending,
    isSubmittingForReview: submitForReviewMutation.isPending,
  };
}
